/**
 * Ads Manager «Split by devices» — the rules the table and the filter
 * strip share: where the item exists, what is remembered, which columns a
 * device sub-row fills and which it leaves blank.
 *
 * The numbers come from the server (`?split=devices` → `row.device_split`); the
 * client never splits anything itself.
 */

import { ALL_COLUMNS, type ColumnKey } from "@/lib/columns-config";

export const DEVICES = ["IPHONE", "IPAD"] as const;
export type DeviceKey = (typeof DEVICES)[number];
export const DEVICE_LABEL: Record<DeviceKey, string> = { IPHONE: "iPhone", IPAD: "iPad" };

export const SPLIT_TITLE = "Split by devices";
export const ADS_TAB_REASON = "Apple doesn't provide device split for ads";

/** Only apps Apple Ads knows as iPad-capable (Campaign Studio's flag). */
export function splitAvailable(app: { ipad_supported?: boolean } | null | undefined): boolean {
  return !!app?.ipad_supported;
}

/** Remembered like the other Ads Manager filters: in localStorage, per
 *  account × app. One flag for every level — a split switched on at
 *  campaigns stays on after the drill into a campaign. */
export function splitStorageKey(accountId: string, appId: number | "all"): string {
  return `oneads:adsDeviceSplit:${accountId}:${appId}`;
}

export function loadSplit(key: string): boolean {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

export function saveSplit(key: string, on: boolean): void {
  try {
    if (on) localStorage.setItem(key, "1");
    else localStorage.removeItem(key);
  } catch {
    /* storage off (private window): the split just isn't remembered */
  }
}

// Not a device property: parameters (status, budget, bid, label, rules…)
// and the app-wide share of voice / rank / popularity of a query.
const NOT_SPLIT: ReadonlySet<string> = new Set([
  "impression_share", "sov_slot2", "sov_total", "search_rank", "sov_2d", "sov_5d",
  "search_ads_popularity", "active_keywords", "active_capacity", "active_share",
  "organic_search", "organic_position",
]);
const CATEGORY = new Map(ALL_COLUMNS.map((c) => [c.key as string, c.category]));

/** A column a device sub-row fills; the rest stay empty in the sub-row. */
export function splitsByDevice(key: ColumnKey | string): boolean {
  if (NOT_SPLIT.has(key)) return false;
  const cat = CATEGORY.get(key);
  // Adjust columns (`adj_*`) aren't in the catalogue — they are events.
  if (cat === undefined) return key.startsWith("adj_");
  return cat !== "parameters";
}

/** Always two sub-rows, in a fixed order. A device the server had no
 *  number for comes back `null` → the sub-row shows dashes. */
export function deviceSubRows<T>(
  devices: Partial<Record<string, T | null>> | null | undefined,
): { device: DeviceKey; label: string; data: T | null }[] {
  return DEVICES.map((d) => ({ device: d, label: DEVICE_LABEL[d], data: devices?.[d] ?? null }));
}

export const DEVICE_TOTAL_LABEL: Record<DeviceKey, string> = {
  IPHONE: "Total for iPhone",
  IPAD: "Total for iPad",
};

/** The footer's two device lines under «Total filtered», iPhone then iPad.
 *  `data` is the server's sum for that device over EVERY filtered entity
 *  (`device_totals` — all pages); `null` = no entity had a number for the
 *  device → dashes, a real zero stays $0.00. */
export function deviceTotalRows<T>(
  totals: Partial<Record<string, T | null>> | null | undefined,
): { device: DeviceKey; label: string; data: T | null }[] {
  return DEVICES.map((d) => ({
    device: d,
    label: DEVICE_TOTAL_LABEL[d],
    data: totals?.[d] ?? null,
  }));
}

/**
 * Which footer row gets the device lines. Only «Total filtered» is ever
 * split; «Total by <App>» never is.
 *  - "filtered": the table draws a separate Total filtered row (Campaigns);
 *  - "single":   the table's only footer row IS the filtered total (Ad
 *                groups / Keywords / Search terms — no Total by <App> there);
 *  - null:       split off, or the server sent no device totals.
 */
export type DeviceTotalsPlacement = "filtered" | "single" | null;

export function deviceTotalsPlacement(opts: {
  splitOn: boolean;
  hasDeviceTotals: boolean;
  filteredRowShown: boolean;
  singleFooter: boolean;
}): DeviceTotalsPlacement {
  if (!opts.splitOn || !opts.hasDeviceTotals) return null;
  if (opts.filteredRowShown) return "filtered";
  return opts.singleFooter ? "single" : null;
}
